export function normalizeFreeTicketRequirements(minFreeTickets, minGoldenTickets) {
  const minFree = Math.max(0, parseInt(minFreeTickets, 10) || 0);
  const minGolden = Math.max(0, parseInt(minGoldenTickets, 10) || 0);
  const valid = minFree > 0 || minGolden > 0;
  return {
    minFreeTickets: minFree,
    minGoldenTickets: minGolden,
    minTotalStake: minFree + minGolden,
    goldenOnly: minFree === 0 && minGolden > 0,
    valid,
  };
}

function word(n, singular) {
  return n === 1 ? singular : `${singular}s`;
}

/**
 * Golden can cover free/normal requirements. Free cannot cover golden.
 * If minFree is 0 and minGolden > 0, only golden tickets may be spent.
 */
export function analyzeFreeTicketAffordability({
  minFreeTickets = 0,
  minGoldenTickets = 0,
  normalHave = 0,
  goldenHave = 0,
  stake = null,
} = {}) {
  const minFree = Math.max(0, parseInt(minFreeTickets, 10) || 0);
  const minGolden = Math.max(0, parseInt(minGoldenTickets, 10) || 0);
  const normal = Math.max(0, parseInt(normalHave, 10) || 0);
  const golden = Math.max(0, parseInt(goldenHave, 10) || 0);
  const minTotal = minFree + minGolden;
  const qty = Math.max(minTotal, parseInt(stake, 10) || minTotal || 1);
  const goldenOnly = minFree === 0 && minGolden > 0;

  if (golden < minGolden) {
    return {
      ok: false,
      code: 'golden_short',
      message: `You need ${minGolden} golden ${word(minGolden, 'ticket')} (you have ${golden}). Free tickets cannot cover the golden requirement.`,
      need: minGolden,
      have: golden,
      short: minGolden - golden,
      goldenOnly,
      minTotal,
    };
  }

  if (goldenOnly) {
    if (golden < qty) {
      return {
        ok: false,
        code: 'golden_only_short',
        message: `This event is golden-ticket only. You need ${qty} golden ${word(qty, 'ticket')} (you have ${golden}).`,
        need: qty,
        have: golden,
        short: qty - golden,
        goldenOnly,
        minTotal,
      };
    }
    return {
      ok: true,
      code: 'ok',
      goldenOnly: true,
      minTotal,
      minFromGolden: qty,
      minFromNormal: 0,
    };
  }

  const leftoverGolden = golden - minGolden;
  const remainingNeed = qty - minGolden;
  const remainingHave = normal + leftoverGolden;
  if (remainingHave < remainingNeed) {
    const haveTotal = normal + golden;
    return {
      ok: false,
      code: 'total_short',
      message:
        minFree > 0 && normal < minFree
          ? `You need ${qty} ${word(qty, 'ticket')} total (${minFree} free can be covered by golden if your free tickets are exhausted). You have ${normal} free and ${golden} golden.`
          : `You need ${qty} ${word(qty, 'ticket')} total (you have ${haveTotal}).`,
      need: qty,
      have: haveTotal,
      short: Math.max(0, qty - haveTotal),
      goldenOnly: false,
      minTotal,
    };
  }

  return {
    ok: true,
    code: 'ok',
    goldenOnly: false,
    minTotal,
    minFromGolden: minGolden,
    minFromNormal: 0,
  };
}

export function formatFreeEntryRequirement(entryReqs = {}) {
  const minFree = Math.max(0, parseInt(entryReqs.minFreeTickets, 10) || 0);
  const minGolden = Math.max(0, parseInt(entryReqs.minGoldenTickets, 10) || 0);
  const minTotal = entryReqs.minTotalStake != null ? entryReqs.minTotalStake : minFree + minGolden;
  if (minFree === 0 && minGolden === 0) {
    return 'Set at least one of min free or min golden tickets.';
  }
  if (entryReqs.goldenOnly || (minFree === 0 && minGolden > 0)) {
    return `This event requires ${minGolden} golden ${word(minGolden, 'ticket')}. Free tickets cannot be used.`;
  }
  if (minGolden === 0) {
    return `This event requires ${minFree} ${word(minFree, 'ticket')}. Golden tickets can be used if your free tickets are exhausted.`;
  }
  return `This event requires at least ${minTotal} ${word(minTotal, 'ticket')} (${minFree} free + ${minGolden} golden). Golden tickets can cover free tickets if you run out, but the golden requirement must be paid with golden tickets.`;
}

export function formatTicketPeriodDays(days) {
  const d = Math.max(1, parseInt(days, 10) || 1);
  if (d === 1) return 'day';
  return `${d} days`;
}

export function formatGrantRate(tickets, periodDays) {
  const qty = Math.max(0, parseInt(tickets, 10) || 0);
  if (qty <= 0) return '—';
  return `+${qty} / ${formatTicketPeriodDays(periodDays)}`;
}
